// Story 11.1: Shared GSAP loader + reduced-motion snapshot.
// Module-scope preference snapshot per architecture-patterns.md Pattern 5 —
// captures the user's `prefers-reduced-motion` setting once at module load so
// every GSAP-driven component can short-circuit to instant state without
// querying `matchMedia` on every render. Tests override via
// `installMatchMediaMock(true)` then `vi.resetModules()` + re-import.

export const prefersReducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

type GsapDefault = typeof import('gsap').default;

let gsapPromise: Promise<GsapDefault> | null = null;

export function loadGsap(): Promise<GsapDefault> {
  if (gsapPromise) return gsapPromise;
  const promise = import('gsap').then((m) => m.default);
  // Evict a rejected promise so callers can retry instead of being stuck
  // with a permanently-poisoned cache after a transient chunk-load failure.
  promise.catch(() => {
    if (gsapPromise === promise) gsapPromise = null;
  });
  gsapPromise = promise;
  return promise;
}

export interface GsapWithPlugin<TPlugin = unknown> {
  gsap: GsapDefault;
  plugin: TPlugin;
}

const pluginPromises = new Map<string, Promise<GsapWithPlugin>>();

// Loads gsap + a plugin module through a single cached entrypoint. The resolved
// value exposes both `gsap` (for tweens whose `scrollTrigger: {...}` config
// reaches the plugin implicitly) and `plugin` (for callers that need the
// plugin's own API directly, e.g. `ScrollTrigger.create(...)`). Keeps the
// "no inline gsap loaders" posture from AC4 of story 11.5 intact.
export function loadGsapWithPlugin<TPlugin = unknown>(
  pluginLoader: () => Promise<Record<string, unknown>>,
  pluginName: string,
): Promise<GsapWithPlugin<TPlugin>> {
  const existing = pluginPromises.get(pluginName);
  if (existing) return existing as Promise<GsapWithPlugin<TPlugin>>;

  const promise = Promise.all([loadGsap(), pluginLoader()]).then(([gsap, pluginModule]) => {
    const plugin = pluginModule[pluginName] ?? pluginModule.default;
    if (!plugin) {
      throw new Error(
        `loadGsapWithPlugin: plugin export "${pluginName}" (or "default") not found in loaded module`,
      );
    }
    gsap.registerPlugin(plugin as Parameters<typeof gsap.registerPlugin>[0]);
    return { gsap, plugin } as GsapWithPlugin<TPlugin>;
  });
  // Evict a rejected promise so a later caller can retry rather than being
  // handed the same permanently-rejected value.
  promise.catch(() => {
    if (pluginPromises.get(pluginName) === promise) pluginPromises.delete(pluginName);
  });
  pluginPromises.set(pluginName, promise);
  return promise;
}
